import { Component, css, html, LitElement, svg } from '@rxdi/lit-html';

interface Marble {
  label: string;
  delay: number;
}

const DURATION = 14;

const ideas: Marble[] = [
  { label: 'a', delay: 0 },
  { label: 'b', delay: 3.4 },
  { label: 'c', delay: 7.1 },
  { label: 'd', delay: 10.6 },
];
const feedback: Marble[] = [
  { label: '1', delay: 1.6 },
  { label: '2', delay: 5.5 },
  { label: '3', delay: 9.2 },
  { label: '4', delay: 12.4 },
];
const product: Marble[] = [
  { label: 'v1', delay: 0.9 },
  { label: 'v2', delay: 4.4 },
  { label: 'v3', delay: 8.0 },
  { label: 'v4', delay: 11.5 },
];

/**
 * The hero's living marble diagram: two input streams flow through an
 * operator into the output stream. Pure SVG + CSS animation (no timers).
 */
@Component({
  selector: 'rs-marbles',
  styles: [
    css`
      :host {
        display: block;
        width: 100%;
      }

      svg {
        display: block;
        width: 100%;
        height: auto;
        overflow: visible;
      }

      .line {
        stroke: var(--rs-line-strong);
        stroke-width: 1.5;
      }

      .tick {
        stroke: var(--rs-line-strong);
        stroke-width: 1.5;
      }

      .label {
        font-family: var(--rs-font-mono);
        font-size: 13px;
        fill: var(--rs-text-muted);
      }

      .label.out {
        fill: var(--rs-signal);
      }

      .op {
        fill: var(--rs-surface);
        stroke: var(--rs-line-strong);
        stroke-width: 1.2;
      }

      .op-text {
        font-family: var(--rs-font-mono);
        font-size: 16px;
        fill: var(--rs-text);
      }

      .op-text .k {
        fill: var(--rs-plasma);
      }

      .op-text .f {
        fill: var(--rs-ion);
      }

      .op-glow {
        fill: none;
        stroke: var(--rs-signal);
        stroke-width: 1.2;
        stroke-dasharray: 6 10;
        opacity: 0.55;
        animation: dash 3s linear infinite;
      }

      .marble {
        animation: flow ${DURATION}s linear infinite;
        animation-fill-mode: both;
      }

      .marble circle {
        stroke-width: 2;
      }

      .marble text {
        font-family: var(--rs-font-mono);
        font-size: 12px;
        font-weight: 600;
        text-anchor: middle;
        dominant-baseline: central;
      }

      .ideas circle {
        fill: var(--rs-ion-soft);
        stroke: var(--rs-ion);
      }
      .ideas text {
        fill: var(--rs-ion);
      }

      .feedback circle {
        fill: var(--rs-plasma-soft);
        stroke: var(--rs-plasma);
      }
      .feedback text {
        fill: var(--rs-plasma);
      }

      .product rect {
        fill: var(--rs-signal);
        filter: drop-shadow(0 0 10px var(--rs-signal-glow));
      }
      .product text {
        fill: var(--rs-text-on-accent);
      }

      @keyframes flow {
        0% {
          transform: translateX(0);
          opacity: 0;
        }
        4% {
          opacity: 1;
        }
        92% {
          opacity: 1;
        }
        100% {
          transform: translateX(1080px);
          opacity: 0;
        }
      }

      @keyframes dash {
        to {
          stroke-dashoffset: -32;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .marble,
        .op-glow {
          animation: none;
        }
        .marble {
          opacity: 1;
        }
      }
    `,
  ],
  template(this: MarblesComponent) {
    return html`
      <svg
        viewBox="0 0 1200 300"
        role="img"
        aria-label="Marble diagram: ideas and feedback streams flow through switchMap(ship) into a product stream"
      >
        <!-- input: ideas$ -->
        <text class="label" x="0" y="44">ideas$</text>
        <line class="line" x1="90" y1="40" x2="1180" y2="40"></line>
        <path class="tick" d="M1172 34 L1182 40 L1172 46" fill="none"></path>
        ${ideas.map((m) => this.circle('ideas', m, 40))}

        <!-- input: feedback$ -->
        <text class="label" x="0" y="104">feedback$</text>
        <line class="line" x1="90" y1="100" x2="1180" y2="100"></line>
        <path class="tick" d="M1172 94 L1182 100 L1172 106" fill="none"></path>
        ${feedback.map((m) => this.circle('feedback', m, 100))}

        <!-- operator -->
        <rect class="op" x="90" y="136" width="1090" height="56" rx="12"></rect>
        <rect class="op-glow" x="90" y="136" width="1090" height="56" rx="12"></rect>
        <text class="op-text" x="635" y="170" text-anchor="middle">
          <tspan class="k">combineLatest</tspan>
          (ideas$, feedback$).pipe(
          <tspan class="f">switchMap</tspan>
          (ship))
        </text>

        <!-- output: product$ -->
        <text class="label out" x="0" y="254">product$</text>
        <line class="line" x1="90" y1="250" x2="1180" y2="250"></line>
        <path class="tick" d="M1172 244 L1182 250 L1172 256" fill="none"></path>
        ${product.map((m) => this.square(m, 250))}
      </svg>
    `;
  },
})
export class MarblesComponent extends LitElement {
  private circle(kind: 'ideas' | 'feedback', m: Marble, y: number) {
    return svg`
      <g class="marble ${kind}" style="animation-delay: -${m.delay}s">
        <circle cx="100" cy=${y} r="15"></circle>
        <text x="100" y=${y}>${m.label}</text>
      </g>
    `;
  }

  private square(m: Marble, y: number) {
    return svg`
      <g class="marble product" style="animation-delay: -${m.delay}s">
        <rect x="83" y=${y - 15} width="34" height="30" rx="8"></rect>
        <text x="100" y=${y}>${m.label}</text>
      </g>
    `;
  }
}
